import { ApolloClient, InMemoryCache } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import env from './env'
import { FirebaseAuth } from 'shared/services'
import { createUploadLink } from 'apollo-upload-client'
import { onError } from '@apollo/client/link/error'
import i18next from 'i18next'
import {
  exitSystemAdminMode,
  viewerCanUseSystemAdminModeVar,
  ViewerMode,
  viewerModeVar,
} from 'shared/utils'
import { GRAPHQL_ERROR_CODES } from 'shared/constants/graphql-errors'

export const getOptimizedAcceptLanguage = () => {
  const defaultLang = 'en'
  const MAX_LANGUAGES = 10

  const isEnglish = (lang: string) => lang.toLowerCase().startsWith('en')

  // return languages list trimmed to max size, making sure at least one "en" stays
  const trimLanguages = (languages: string[], max: number) => {
    // only trim to max size if languages list is longer than max
    while (languages.length > max) {
      const last = languages.at(-1)!

      // if the last is not English, just remove it
      if (!isEnglish(last)) {
        languages.pop()
        continue
      }

      // if the last is English, try to remove an earlier non-English
      let removed = false
      for (let i = languages.length - 2; i >= 0; i--) {
        if (!isEnglish(languages[i])) {
          languages.splice(i, 1)
          removed = true
          break
        }
      }

      // if all are English, remove the last one (if the array exceeds max size)
      if (!removed) {
        languages.pop()
      }
    }

    return languages
  }

  // get selected language in app
  let selected = i18next.language || defaultLang

  // get browser languages
  const browserLangs =
    Array.isArray(navigator.languages) && navigator.languages.length
      ? navigator.languages
      : navigator.language
      ? [navigator.language]
      : []

  // the most important language is always first (our selected language in BB/GW)
  let ordered = Array.from(
    new Set([
      selected,
      ...browserLangs.filter(l => l.toLowerCase() !== selected.toLowerCase()),
    ])
  )

  if (!ordered.some(isEnglish)) {
    ordered.push('en') // fallback to English, if no English is found
  }

  ordered = trimLanguages(ordered, MAX_LANGUAGES) // trim to max size, making sure at least one "en" stays

  return ordered
    .map(
      (lang, index) =>
        index === 0
          ? lang // the most important language without q
          : `${lang};q=${Math.max(1 - index * 0.1, 0.1).toFixed(1)}` // lower q for less important languages
    )
    .join(',')
}

const httpLink = createUploadLink({
  uri: `${env.apiUrl}/graphql`,
  credentials: 'same-origin',
})

const errorLink = onError(({ graphQLErrors }) => {
  const hasForbiddenError = graphQLErrors?.some(
    error => error.extensions?.code === GRAPHQL_ERROR_CODES.FORBIDDEN
  )

  if (hasForbiddenError && viewerModeVar() === ViewerMode.SYSTEM_ADMIN) {
    exitSystemAdminMode()
  }
})

export const getDataClient = (auth: FirebaseAuth) => {
  const authLink = setContext(async (_, { headers }) => {
    let authToken = ''
    try {
      const authTokenResult = await auth.getIdToken()
      if (authTokenResult) {
        authToken = authTokenResult
      }
    } catch (e) {
      console.error(e)
    }
    let appCheckToken = ''
    try {
      const result = await auth.getAppCheckToken()
      if (result) {
        appCheckToken = result.token
      }
    } catch (e) {
      console.error(e)
    }

    const viewerMode = viewerModeVar()
    const viewerCanUseSystemAdminMode = viewerCanUseSystemAdminModeVar()

    const systemAdminModeEnabled =
      viewerMode === ViewerMode.SYSTEM_ADMIN &&
      viewerCanUseSystemAdminMode !== false

    return {
      headers: {
        ...headers,
        authorization: authToken ? `Bearer ${authToken}` : '',
        'X-App-Identifier': env.appIdentifier,
        'Accept-Language': getOptimizedAcceptLanguage(),
        ...(appCheckToken && { 'X-AppCheck-Token': appCheckToken }),
        'Apollo-Require-Preflight': 'true',
        'X-System-Admin-Mode': String(systemAdminModeEnabled),
      },
    }
  })
  const client = new ApolloClient({
    link: errorLink.concat(authLink).concat(httpLink),
    cache: new InMemoryCache(),
  })
  return client
}
